import Button from '@orn/shared-web/components/Button/Button';
import { OTPInput } from '@orn/shared-web/components/Input';
import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { AlertCircleIcon, CheckCircleIcon } from '@orn/shared-web/icons';

import { useCurrentUser } from '../../../hooks/queries/useCurrentUser';
import useFormattedPhone from '../../../hooks/useFormattedPhone';
import usePhoneVerification from '../../../hooks/usePhoneVerification';
import { OnboardingTitle } from '../../OnboardingLayout';

import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';

/**
 * SMS-verification screen for the new onboarding flow. Owns the full OTP
 * lifecycle (send, resend countdown, verify, skip) on top of the phone number
 * persisted by {@link PhoneNumberEntryStep}.
 *
 * Renders one of three states based on the user's `phoneNumberVerifiedAt`:
 *
 * - **Already verified** — confirmation message + Continue. Reached on back-nav
 *   from a later screen, on resume, or when the user re-submits the same
 *   number from entry (server doesn't clear verification when the value
 *   doesn't change).
 * - **Unverified, pre-send** — "We'll send a verification code to XXX…" copy
 *   plus a Send code button. The user can also Skip to advance without
 *   verifying. A failed send (rate limit, number already in use) surfaces an
 *   inline error card rather than failing silently.
 * - **Unverified, code sent** — OTP input, resend countdown. On the happy
 *   path neither Skip nor the back arrow is offered: tapping Send code is the
 *   user's commitment to complete the loop, and a half-finished attempt is
 *   more confusing than a clean entry-then-resubmit. Once verification errors
 *   out, both reappear so a stuck user can fix the number (back) or bypass
 *   verification (skip) instead of being trapped on a failing code.
 *
 * Owning the send call here (rather than firing it from entry) means a phone
 * edit followed by no-OTP doesn't leave a stranded code in flight, and the
 * "already verified" branch can short-circuit the SMS round-trip entirely.
 *
 * Figma: Mantle - UX/UI — shares the OTP-cell visual from node 4142:67530
 * ("Onboarding Step 02"); copy is phone-specific.
 */
const PhoneNumberVerifyStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { user } = useCurrentUser();
  const phoneVerification = usePhoneVerification(onNext);
  const rawPhone = user?.phoneNumber ?? '';
  const formattedPhone = useFormattedPhone(rawPhone);
  const isAlreadyVerified = user?.phoneNumberVerifiedAt != null;

  if (isAlreadyVerified) {
    return (
      <>
        <OnboardingTitle title="Phone already verified" onBack={onBack} />

        <div className="flex w-full flex-col gap-4">
          <MessagingCard variant="success">
            <MessagingCard.Text icon={<CheckCircleIcon />}>
              <span className="text-primary font-bold">{formattedPhone}</span> is already verified — you&apos;re all
              set.
            </MessagingCard.Text>
          </MessagingCard>
        </div>

        <OnboardingStepActions onContinue={onNext} />
      </>
    );
  }

  if (!phoneVerification.codeSent) {
    return (
      <>
        <OnboardingTitle title="Verify your phone" onBack={onBack} />

        <div className="flex w-full flex-col gap-4">
          <span className="text-tertiary text-md w-full font-normal">
            We&apos;ll send a verification code to <span className="text-primary font-bold">{formattedPhone}</span> to
            confirm your number.
          </span>

          {phoneVerification.error && (
            <MessagingCard variant="error">
              <MessagingCard.Text icon={<AlertCircleIcon />}>{phoneVerification.error}</MessagingCard.Text>
            </MessagingCard>
          )}
        </div>

        <OnboardingStepActions
          continueLabel="Send code"
          isContinueLoading={phoneVerification.isSending}
          onContinue={() => void phoneVerification.handleSendCode()}
          onSkip={onNext}
        />
      </>
    );
  }

  // A failed verification is the only escape hatch out of the code-sent state:
  // surface back (fix the number) + skip (bypass) so the user isn't trapped.
  const hasError = Boolean(phoneVerification.error);

  return (
    <>
      <OnboardingTitle
        title="Check your phone"
        onBack={hasError ? onBack : undefined}
        subtitle={
          <span className="text-tertiary text-md w-full font-normal">
            We sent a 6-digit code to <span className="text-primary font-bold">{formattedPhone}</span>. Enter it below.
          </span>
        }
      />

      <div className="flex w-full flex-col items-center gap-4">
        <OTPInput
          value={phoneVerification.code}
          onChange={phoneVerification.setCode}
          onComplete={(code) => void phoneVerification.handleVerify(code)}
          isInvalid={hasError}
          errorMessage={phoneVerification.error || undefined}
          isDisabled={phoneVerification.isVerifying}
          autoFocus
        />

        <div className="flex items-center gap-1">
          {phoneVerification.isCountdownActive ? (
            <span className="text-tertiary text-sm">Resend code in {phoneVerification.secondsRemaining}s</span>
          ) : (
            <Button
              color="link-color"
              size="sm"
              onClick={() => void phoneVerification.handleSendCode()}
              isDisabled={phoneVerification.isSending}
            >
              Resend code
            </Button>
          )}
        </div>
      </div>

      {hasError && (
        <Button type="button" size="md" color="link-gray" className="self-center" onClick={onNext}>
          Skip for now
        </Button>
      )}
    </>
  );
};

PhoneNumberVerifyStep.displayName = 'PhoneNumberVerifyStep';

export default PhoneNumberVerifyStep;
